import { Link } from 'react-router-dom';
import type { SearchLang, SearchResult as SearchResultType } from '@/types/api';
import { HighlightAllahText } from '@/components/ui/HighlightAllahText';
import { cn } from '@/lib/utils/cn';

type Props = {
  result: SearchResultType;
  lang: SearchLang;
};

/**
 * Single search result card. Renders the ayah reference + a snippet
 * (with `<mark>` highlights parsed safely into React elements) + the
 * Arabic text always, plus the translation matching the chosen lang.
 */
export function SearchResult({ result, lang }: Props) {
  const ref = `${result.surah_id}:${result.ayah_number}`;
  const translation =
    lang === 'ur' ? result.translation_ur : result.translation_en;

  return (
    <Link
      to={`/surah/${result.surah_id}/ayah/${result.ayah_number}`}
      className={cn(
        'block py-6 -mx-3 px-3 rounded-lg',
        'transition-colors hover:bg-ink-100/60 dark:hover:bg-ink-900/40',
      )}
    >
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <span className="font-serif text-sm text-ink-500 dark:text-ink-400 num-tight">
          {ref}
        </span>
        {result.similarity !== undefined && (
          <span className="text-[10px] uppercase tracking-[0.18em] text-ink-400 dark:text-ink-500">
            {Math.round(result.similarity * 100)}% match
          </span>
        )}
      </div>

      {result.snippet ? (
        <p
          className={cn(
            'leading-relaxed mb-3',
            lang === 'ur'
              ? 'urdu-display text-base'
              : 'text-ink-800 dark:text-ink-100 font-serif text-lg',
          )}
          dir={lang === 'ur' || lang === 'ar' ? 'rtl' : 'ltr'}
        >
          {renderSnippet(result.snippet)}
        </p>
      ) : translation ? (
        <p
          className={cn(
            'leading-relaxed mb-3',
            lang === 'ur'
              ? 'urdu-display text-base'
              : 'text-ink-800 dark:text-ink-100 font-serif text-lg',
          )}
          dir={lang === 'ur' ? 'rtl' : 'ltr'}
        >
          <HighlightAllahText text={truncate(translation, 220)} />
        </p>
      ) : null}

      <p className="arabic-display text-xl sm:text-2xl mt-3 leading-[2]">
        <HighlightAllahText text={truncate(result.text_ar, 180)} />
      </p>
    </Link>
  );
}

/**
 * Parse `ts_headline` output into React. We accept only `<mark>` and
 * `</mark>` as recognized tags; everything else renders as text. This
 * is intentionally stricter than `dangerouslySetInnerHTML` would be.
 */
function renderSnippet(snippet: string) {
  const parts = snippet.split(/(<mark>|<\/mark>)/g);
  let inMark = false;
  return parts.map((part, i) => {
    if (part === '<mark>') {
      inMark = true;
      return null;
    }
    if (part === '</mark>') {
      inMark = false;
      return null;
    }
    return inMark ? (
      <mark
        key={i}
        className="bg-accent/15 text-accent-700 dark:text-accent-300 rounded px-0.5"
      >
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    );
  });
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}
